import React, { useEffect } from 'react';
import { Helmet } from "react-helmet-async";
import { Sparkles, Calendar, Mail, Phone, UserCheck, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function AdmissionDetails() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="py-5" style={{ background: '#f8fafc', minHeight: '85vh' }}>
      <Helmet>
        <title>Admission Details &amp; Guidelines - Vidyajyoti College of Theology</title>
        <meta name="description" content="Admission guidelines, eligibility criteria, and important dates for B.Th., M.Th., D.Th., Diploma, and Certificate courses at Vidyajyoti." />
        <meta name="keywords" content="Vidyajyoti Admission, Theology admission Delhi, BTh admission, MTh admission, Seminary admission" />
        <link rel="canonical" href="/admission/details" />
      </Helmet>

      <div className="container">
        {/* Hero Card */}
        <div className="card border-0 rounded-4 shadow-sm p-4 p-md-5 mb-5" style={{ background: 'linear-gradient(135deg, #1a2b56 0%, #2a3f75 100%)', color: '#fff' }}>
          <div className="row align-items-center g-4">
            <div className="col-lg-8">
              <div className="d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill mb-3" style={{ background: 'rgba(198, 146, 20, 0.2)', color: '#ffd56b', fontSize: '0.85rem', fontWeight: 600 }}>
                <Sparkles size={14} /> Academic Year Admissions
              </div>
              <h1 className="display-6 fw-bold mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Admission Details &amp; Guidelines
              </h1>
              <p className="lead mb-3 text-light opacity-90" style={{ fontSize: '1.05rem', lineHeight: '1.7' }}>
                VIDYAJYOTI invites applications for admission to undergraduate, postgraduate, doctoral, and modular programmes: <strong>B.Th. (Degree, Certificate &amp; Diploma), M.Th. (S.T.L.), and Ph.D. / D.Th.</strong> at Vidyajyoti Faculty of Theology, 4A Raj Niwas Marg, Delhi – 110 054.
              </p>
              <div className="d-inline-flex align-items-center gap-2 px-3 py-2 rounded-3" style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)' }}>
                <Calendar size={18} className="text-warning" />
                <span className="small text-white">The Academic Year 2026–2027 begins on <strong>Monday, 06 July 2026</strong>.</span>
              </div>
            </div>

            <div className="col-lg-4 text-center">
              <div className="p-4 rounded-4 bg-white text-dark shadow">
                <UserCheck size={36} className="mb-2" style={{ color: '#79031d' }} />
                <h5 className="fw-bold mb-2" style={{ color: '#1a2b56' }}>Apply Online</h5>
                <p className="text-muted small mb-3">Begin your application submission through our digital admission portal.</p>
                <Link to="/admission/application-form" className="btn btn-sm w-100 rounded-pill fw-semibold text-white py-2" style={{ background: '#79031d' }}>
                  Open Application Form <ArrowRight size={14} className="ms-1" />
                </Link>
                <Link to="/admission/application-status" className="btn btn-sm w-100 rounded-pill fw-semibold text-dark mt-2 bg-light border">
                  Check Application Status
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Admission Guidelines & Statutes */}
        <div className="card border-0 rounded-4 shadow-sm p-4 p-md-5 bg-white mb-5">
          <div className="d-flex align-items-center gap-2 mb-4">
            <span className="badge px-3 py-1 rounded-pill" style={{ background: '#79031d', color: '#fff' }}>Statutes &amp; Regulations</span>
            <h3 className="fw-bold mb-0" style={{ color: '#1a2b56' }}>Admission Norms (Veritatis Gaudium)</h3>
          </div>

          <div className="row g-4">
            {[
              {
                num: "01",
                title: "Universal Openness",
                text: "In keeping with the Apostolic Constitution Veritatis Gaudium (V.G. 31) and Vidyajyoti Statutes (5,2), the Institute is in principle open to all students—clerical, religious, and lay—who are duly qualified."
              },
              {
                num: "02",
                title: "Admission Board Sanction",
                text: "Admissions are granted by the Principal upon recommendation of the Admissions Board, following examination of application dossiers, character references, and academic records (Art. 26,1)."
              },
              {
                num: "03",
                title: "Philosophy Prerequisite",
                text: "Candidates seeking admission for the first theological degree (B.Th.) must have completed two years of philosophical studies in a recognized faculty or institute (Art. 26,3)."
              },
              {
                num: "04",
                title: "Mandatory Attendance",
                text: "Regular attendance at lectures and active participation in prescribed seminars/fieldwork are mandatory. These norms apply mutatis mutandis to Diploma and Certificate students (Art. 28,1)."
              },
              {
                num: "05",
                title: "Examination Eligibility",
                text: "Students missing more than one-third of scheduled classes without written approval may be debarred from terminal examinations by the Principal in consultation with the Executive Board."
              }
            ].map((rule, idx) => (
              <div key={idx} className="col-md-6">
                <div className="p-4 rounded-4 h-100 border bg-light" style={{ borderLeft: '4px solid #1a2b56' }}>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <span className="badge rounded-circle p-2" style={{ background: '#1a2b56', color: '#ffd56b', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem' }}>
                      {rule.num}
                    </span>
                    <h5 className="fw-bold mb-0" style={{ color: '#1a2b56', fontSize: '1.05rem' }}>{rule.title}</h5>
                  </div>
                  <p className="text-muted small mb-0" style={{ lineHeight: '1.6' }}>{rule.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Contact & Helpdesk Information */}
        <div className="card border-0 rounded-4 shadow-sm p-4 p-md-5" style={{ background: 'linear-gradient(135deg, rgba(26,43,86,0.04) 0%, rgba(198,146,20,0.06) 100%)', border: '1px solid rgba(26,43,86,0.1)' }}>
          <div className="row align-items-center g-4">
            <div className="col-lg-6">
              <h4 className="fw-bold mb-3" style={{ color: '#1a2b56' }}>Admission Helpdesk &amp; Inquiries</h4>
              <p className="text-muted small mb-3">If you have questions regarding application procedures, eligibility, or documentation:</p>

              <div className="d-flex flex-column gap-2">
                <div className="d-flex align-items-center gap-2">
                  <Mail size={16} className="text-primary" />
                  <span className="small text-muted"><strong>Registrar:</strong> <a href="mailto:vjregis@gmail.com" className="text-decoration-none" style={{ color: '#79031d' }}>vjregis@gmail.com</a></span>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <Mail size={16} className="text-primary" />
                  <span className="small text-muted"><strong>Principal:</strong> <a href="mailto:principalvj@gmail.com" className="text-decoration-none" style={{ color: '#79031d' }}>principalvj@gmail.com</a></span>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <Phone size={16} className="text-success" />
                  <span className="small text-muted"><strong>Technical Helpline:</strong> <a href="tel:+919971186877" className="text-decoration-none text-dark fw-semibold">(+91) 99711 86877</a></span>
                </div>
              </div>
            </div>

            <div className="col-lg-6 text-lg-end">
              <div className="p-4 rounded-4 bg-white shadow-sm border d-inline-block text-start" style={{ maxWidth: '400px' }}>
                <h5 className="fw-bold mb-1" style={{ color: '#1a2b56' }}>Prof. Dr. Fr. Rajakumar Joseph SJ</h5>
                <p className="text-muted small mb-0">Principal, Vidyajyoti College of Theology</p>
                <p className="text-muted small mb-0">Institute for Religious Studies</p>
                <p className="text-muted small mb-0">4A Raj Niwas Marg, Delhi – 110 054</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
